import React, {useEffect, useState} from "react";
import UserModal from "./UserModal";
import { isSuperAdmin } from "../utils/roleUtils";
import { apiRequest } from "../api";

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [universities, setUniversities] = useState([]);
    const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
    const [deletingUserId, setDeletingUserId] = useState(null);
    
    // Filter and sort state
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [universityFilter, setUniversityFilter] = useState('');
    const [sortBy, setSortBy] = useState('first_name');
    const [sortOrder, setSortOrder] = useState('asc');

    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const universityId = localStorage.getItem("universityId");
    const token = localStorage.getItem("token");

    const fetchUsers = async () => {
        try {
            const endpoint =
                isSuperAdmin()
                    ? `${apiUrl}/users`
                    : `${apiUrl}/users/university/${universityId}`;

            // Build query parameters
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (roleFilter) params.append('role', roleFilter);
            if (activeFilter !== '') params.append('is_active', activeFilter);
            if (universityFilter) params.append('university_id', universityFilter);
            if (sortBy) params.append('sort_by', sortBy);
            if (sortOrder) params.append('sort_order', sortOrder);

            const url = `${endpoint}?${params.toString()}`;

            const data = await apiRequest(url, "GET", null, token);
            setUsers(data);
        } catch (error) {
            console.error("Error fetching users:", error);
            setError("Failed to fetch users.");
        }
    };

    const fetchUniversities = async () => {
        if (isSuperAdmin()) {
            setIsLoadingUniversities(true);
            try {
                const data = await apiRequest(`${apiUrl}/universities`, "GET", null, token);
                setUniversities(data);
            } catch (error) {
                console.error("Error fetching universities:", error);
                setUniversities([]);
            } finally {
                setIsLoadingUniversities(false);
            }
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchUniversities();
    }, [universityId, searchTerm, roleFilter, activeFilter, universityFilter, sortBy, sortOrder]);

    const handleDisable = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}/disable`, "PUT", null, token);

            setUsers(users.map(user => user.id === userId ? {...user, is_active: false} : user));
            setFeedbackMessage("User disabled successfully.");
        } catch (error) {
            console.error("Error disabling user:", error);
            setFeedbackMessage("Failed to disable user. Please try again.");
        }

        setTimeout(() => setFeedbackMessage(null), 3000);
    };

    const handleActivate = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}/activate`, "PUT", null, token);

            setUsers(users.map(user => user.id === userId ? {...user, is_active: true} : user));
            setFeedbackMessage("User activated successfully.");
        } catch (error) {
            console.error("Error activating user:", error);
            setFeedbackMessage("Failed to activate user. Please try again.");
        }

        setTimeout(() => setFeedbackMessage(null), 3000);
    };

    const handleUpgrade = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}/upgrade`, "PUT", null, token);

            setUsers(users.map(user => user.id === userId ? {...user, multi_user: true} : user));
            setFeedbackMessage("User upgraded successfully.");
        } catch (error) {
            console.error("Error upgrading user:", error);
            setFeedbackMessage("Failed to upgrade user. Please try again.");
        }

        setTimeout(() => setFeedbackMessage(null), 3000);
    };

    const handleDowngrade = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}/downgrade`, "PUT", null, token);

            setUsers(users.map(user => user.id === userId ? {...user, multi_user: false} : user));
            setFeedbackMessage("User downgraded successfully.");
        } catch (error) {
            console.error("Error downgrading user:", error);
            setFeedbackMessage("Failed to downgrade user. Please try again.");
        }

        setTimeout(() => setFeedbackMessage(null), 3000);
    };

    const handleDelete = async (userId) => {
        setDeletingUserId(userId);
        try {
            await apiRequest(`${apiUrl}/users/${userId}`, "DELETE", null, token);

            setUsers(users.filter(user => user.id !== userId));
            setFeedbackMessage("User deleted successfully.");
        } catch (error) {
            console.error("Error deleting user:", error);
            setFeedbackMessage("Failed to delete user. Please try again.");
        } finally {
            setDeletingUserId(null);
        }

        setTimeout(() => setFeedbackMessage(null), 3000);
    };

    const handleSave = async () => {
        setShowModal(false);
        setFeedbackMessage("User saved successfully!");
        await fetchUsers();
        setTimeout(() => setFeedbackMessage(null), 3000);
    };

    return (
        <div>
            {feedbackMessage && (
                <p className={`alert ${feedbackMessage.includes("Failed") ? "alert-danger" : "alert-success"}`}>
                    {feedbackMessage}
                </p>
            )}
            {error && <p className="text-danger">{error}</p>}
            
            {/* Filter and Sort Controls */}
            <div className="row mb-3">
                <div className="col-md-2">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="col-md-2">
                    <select
                        className="form-control"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <option value="">All Roles</option>
                        <option value="super_admin">Super Admin</option>
                        <option value="uni_admin">University Admin</option>
                        <option value="multi_user">Multi User</option>
                        <option value="user">User</option>
                    </select>
                </div>
                <div className="col-md-2">
                    <select
                        className="form-control"
                        value={activeFilter}
                        onChange={(e) => setActiveFilter(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Disabled</option>
                    </select>
                </div>
                {isSuperAdmin() && (
                    <div className="col-md-2">
                        <select
                            className="form-control"
                            value={universityFilter}
                            onChange={(e) => setUniversityFilter(e.target.value)}
                        >
                            <option value="">All Universities</option>
                            {universities.map(university => (
                                <option key={university.id} value={university.id}>
                                    {university.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="col-md-1">
                    <select
                        className="form-control"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <option value="first_name">Name</option>
                        <option value="email">Email</option>
                        <option value="role">Role</option>
                        <option value="university">University</option>
                        <option value="created_on">Created Date</option>
                    </select>
                </div>
                <div className="col-md-1">
                    <select
                        className="form-control"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                    >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                    </select>
                </div>
                <div className="col-md-1">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setSearchTerm('');
                            setRoleFilter('');
                            setActiveFilter('');
                            setUniversityFilter('');
                            setSortBy('first_name');
                            setSortOrder('asc');
                        }}
                    >
                        Clear
                    </button>
                </div>
            </div>
            
            <table className="table table-striped">
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    {isSuperAdmin() && <th>University</th>}
                    <th>Status</th>
                    <th>State</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {users.map(user => (
                    <tr key={user.id}>
                        <td>{`${user.first_name} ${user.last_name}`}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        {isSuperAdmin() && <td>{user.university?.name || "N/A"}</td>}
                        <td>{user.is_active ? "Active" : "Disabled"}</td>
                        <td>{user.multi_user ? "Multi User" : "Single User"}</td>
                        <td>
                            {/* Edit Button - Always Active */}
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                    setSelectedUser(user);
                                    setShowModal(true);
                                }}
                            >
                                Edit
                            </button>
                            &nbsp;

                            {/* Disable Button - Active if user is active */}
                            <button
                                className={`btn btn-sm ${user.is_active ? "btn-warning" : "btn-secondary"}`}
                                onClick={() => handleDisable(user.id)}
                                disabled={!user.is_active}
                            >
                                Disable
                            </button>
                            &nbsp;

                            {/* Activate Button - Active if user is inactive */}
                            <button
                                className={`btn btn-sm ${user.is_active ? "btn-secondary" : "btn-success"}`}
                                onClick={() => handleActivate(user.id)}
                                disabled={user.is_active}
                            >
                                Activate
                            </button>
                            &nbsp;

                            {/* Upgrade Button - Active if multi_user is false */}
                            <button
                                className={`btn btn-sm ${user.multi_user ? "btn-secondary" : "btn-primary"}`}
                                onClick={() => handleUpgrade(user.id)}
                                disabled={user.multi_user}
                            >
                                Upgrade
                            </button>
                            &nbsp;

                            {/* Downgrade Button - Active if multi_user is true */}
                            <button
                                className={`btn btn-sm ${user.multi_user ? "btn-success" : "btn-secondary"}`}
                                onClick={() => handleDowngrade(user.id)}
                                disabled={!user.multi_user}
                            >
                                Downgrade
                            </button>
                            &nbsp;

                            {/* Delete Button - Always Active */}
                            <button 
                                className="btn btn-sm btn-danger" 
                                onClick={() => handleDelete(user.id)}
                                disabled={deletingUserId === user.id}
                            >
                                {deletingUserId === user.id ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {showModal && (
                <UserModal
                    user={selectedUser}
                    universities={universities} // Pass the universities
                    onClose={() => setShowModal(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default UserList;